import { __rest } from 'tslib';
import React from 'react';
import { styled, SubTitle, CommonStyles } from '@teko/ds-foundation';
import { BackOutlined, DotOutlined, ForwardOutlined } from '@teko/ds-icon';

const SubTitle13 = styled(SubTitle) ``;
const BasePage = styled.div `
  width: 2rem;
  height: 2rem;
  display: flex;
  justify-content: center;
  align-items: center;
`;
const InteractPage = styled(BasePage)(({ theme, active, disabled, color }) => `
border-radius: ${theme.borderRadius.s};
background-color: ${active ? theme.colors[color] || color : theme.colors.white};
border: ${active ? 'none' : `1px solid ${theme.colors.divider}`};
pointer-events: ${disabled ? 'none' : 'auto'};
${!active
    ? `
  cursor: pointer;
  &:hover {
    path[stroke],
    rect[stroke],
    line {
      stroke: ${theme.colors.white};
    }
    path:not([stroke]),
    rect:not([stroke]) {
      fill: ${theme.colors.white};
    }
    ${SubTitle13} {
      color: ${theme.colors.white};
    }
    background-color: ${theme.colors.background};
  }`
    : `
  ${SubTitle13} {
    color: ${theme.colors.white};
  }
`}
`);
const PaginationWrap = styled.div `
  display: inline-flex;
  > *:not(:first-of-type) {
    margin-left: ${({ theme }) => theme.spacing.s8};
  }
`;
const StyledPagination = styled.div `
  ${CommonStyles}
  width: 100%;
  text-align: ${(p) => p.align};
`;
const range = (from, to) => {
    return Array.from({ length: to - from + 1 }, (v, k) => k + from);
};
const LEFT_PAGE = 'LEFT';
const RIGHT_PAGE = 'RIGHT';
const generatePageNumbers = (totalPages, currentPage, pageNeighbor) => {
    const totalNumbers = pageNeighbor * 2 + 3;
    const totalBlocks = totalNumbers + 2;
    if (totalPages > totalBlocks) {
        let pages = [];
        const curr = currentPage > totalPages ? totalPages : currentPage;
        const leftBound = curr - pageNeighbor;
        const rightBound = curr + pageNeighbor;
        const beforeLastPage = totalPages - 1;
        const startPage = leftBound > 2 ? leftBound : 2;
        const endPage = rightBound < beforeLastPage ? rightBound : beforeLastPage;
        pages = range(startPage, endPage);
        const pagesCount = pages.length;
        const singleSpillOffset = totalNumbers - pagesCount - 1;
        const leftSpill = startPage > 2;
        const rightSpill = endPage < beforeLastPage;
        const leftSpillPage = LEFT_PAGE;
        const rightSpillPage = RIGHT_PAGE;
        if (leftSpill && !rightSpill) {
            const extraPages = range(startPage - singleSpillOffset, startPage - 1);
            pages = [leftSpillPage, ...extraPages, ...pages];
        }
        else if (!leftSpill && rightSpill) {
            const extraPages = range(endPage + 1, endPage + singleSpillOffset);
            pages = [...pages, ...extraPages, rightSpillPage];
        }
        else if (leftSpill && rightSpill) {
            pages = [leftSpillPage, ...pages, rightSpillPage];
        }
        return [1, ...pages, totalPages];
    }
    return range(1, totalPages);
};
const Pagination = (_a) => {
    var { totalRecords, currentPage, pageLimit = 30, pageNeighbor = 2, onPageChange, align = 'center', color = 'primary500' } = _a, rest = __rest(_a, ["totalRecords", "currentPage", "pageLimit", "pageNeighbor", "onPageChange", "align", "color"]);
    const totalPages = Math.ceil(totalRecords / pageLimit);
    const gotoPage = (page) => {
        onPageChange(page);
    };
    const onBackward = () => {
        gotoPage(currentPage - 1);
    };
    const onForward = () => {
        gotoPage(currentPage + 1);
    };
    const pages = generatePageNumbers(totalPages, currentPage, pageNeighbor);
    return (React.createElement(StyledPagination, Object.assign({ align: align }, rest),
        React.createElement(PaginationWrap, null,
            React.createElement(InteractPage, { color: color, disabled: currentPage === 1, onClick: onBackward },
                React.createElement(BackOutlined, { size: 16, color: "placeholder" })),
            pages.map((page, index) => {
                if ([LEFT_PAGE, RIGHT_PAGE].includes(page)) {
                    return (React.createElement(BasePage, { key: index },
                        React.createElement(DotOutlined, { size: 16, color: "border" })));
                }
                const isActive = page === currentPage;
                return (React.createElement(InteractPage, { color: color, active: isActive, key: index, onClick: isActive ? undefined : () => onPageChange(page) },
                    React.createElement(SubTitle13, { level: 4, color: isActive ? 'white' : 'textPrimary' }, page)));
            }),
            React.createElement(InteractPage, { color: color, disabled: currentPage === totalPages, onClick: onForward },
                React.createElement(ForwardOutlined, { size: 16, color: "placeholder" })))));
};

export { Pagination as P };
